import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  List,
  ListItem,
  Divider,
  IconButton,
  AppBar,
  Toolbar,
  Stack,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Add as AddIcon,
  ConfirmationNumber as TicketIcon,
  Chat as ChatIcon,
  Tag as TagIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

// Sample users for tagging
const availableUsers = [
  { id: 1, name: 'Dr. Sarah Johnson', role: 'Cardiologist', avatar: 'SJ' },
  { id: 2, name: 'Dr. Michael Chen', role: 'General Physician', avatar: 'MC' },
  { id: 3, name: 'Dr. Emily Wilson', role: 'Pathologist', avatar: 'EW' },
  { id: 4, name: 'Dr. Robert Davis', role: 'Orthopedic', avatar: 'RD' },
  { id: 5, name: 'Dr. Lisa Thompson', role: 'Neurologist', avatar: 'LT' },
  { id: 6, name: 'Dr. James Wilson', role: 'Radiologist', avatar: 'JW' },
  { id: 7, name: 'Support Team', role: 'Customer Service', avatar: 'ST' },
  { id: 8, name: 'Admin John', role: 'Administrator', avatar: 'AJ' }
];

// Sample ticket data with multiple tickets per mobile number
const ticketsData = {
  '+91 98765 43210': [
    {
      id: 'TKT-001',
      service: 'Second Opinion',
      status: 'Open',
      priority: 'High',
      createdAt: '2024-01-15 14:30',
      messages: [
        {
          id: 1,
          sender: 'patient',
          message: 'Hello, I need a second opinion for my cardiology report. Can you help me?',
          timestamp: '2024-01-15 14:30',
          avatar: <PersonIcon />,
          tags: []
        },
        {
          id: 2,
          sender: 'support',
          name: 'Dr. Sarah Johnson',
          message: 'Hello! Of course, I can help you with a second opinion. Could you please share your cardiology report details?',
          timestamp: '2024-01-15 14:35',
          avatar: <SupportIcon />,
          tags: [availableUsers[0]] // Dr. Sarah Johnson
        },
        {
          id: 3,
          sender: 'patient',
          message: 'I have been diagnosed with mild heart valve issues. My doctor suggested medication but I want to explore other options.',
          timestamp: '2024-01-15 14:40',
          avatar: <PersonIcon />,
          tags: []
        },
        {
          id: 4,
          sender: 'support',
          name: 'Dr. Sarah Johnson',
          message: 'I understand your concern. Let me consult with @DrMichael for a cardiology review.',
          timestamp: '2024-01-15 14:45',
          avatar: <SupportIcon />,
          tags: [availableUsers[0], availableUsers[1]] // Dr. Sarah Johnson, Dr. Michael Chen
        }
      ]
    },
    {
      id: 'TKT-002',
      service: 'Appointment Booking',
      status: 'Open',
      priority: 'Medium',
      createdAt: '2024-01-15 16:45',
      messages: [
        {
          id: 1,
          sender: 'patient',
          message: 'Hi, I need to book an appointment for a general consultation.',
          timestamp: '2024-01-15 16:45',
          avatar: <PersonIcon />,
          tags: []
        },
        {
          id: 2,
          sender: 'support',
          name: 'Dr. Michael Chen',
          message: 'Hello! I can help you book an appointment. What type of consultation do you need?',
          timestamp: '2024-01-15 16:50',
          avatar: <SupportIcon />,
          tags: [availableUsers[1]] // Dr. Michael Chen
        },
        {
          id: 3,
          sender: 'patient',
          message: 'I have been having headaches for the past week and need to see a general physician.',
          timestamp: '2024-01-15 16:55',
          avatar: <PersonIcon />,
          tags: []
        },
        {
          id: 4,
          sender: 'support',
          name: 'Dr. Michael Chen',
          message: 'I understand. Let me check our available slots. @AdminJohn, can you check the schedule for this week?',
          timestamp: '2024-01-15 17:00',
          avatar: <SupportIcon />,
          tags: [availableUsers[1], availableUsers[7]] // Dr. Michael Chen, Admin John
        }
      ]
    },
    {
      id: 'TKT-003',
      service: 'Health Checkup',
      status: 'Closed',
      priority: 'Low',
      createdAt: '2024-01-14 10:20',
      messages: [
        {
          id: 1,
          sender: 'patient',
          message: 'Hello, I want to schedule a comprehensive health checkup.',
          timestamp: '2024-01-14 10:20',
          avatar: <PersonIcon />,
          tags: []
        },
        {
          id: 2,
          sender: 'support',
          name: 'Dr. Emily Wilson',
          message: 'Hello! I can help you schedule a health checkup. What type of package are you interested in?',
          timestamp: '2024-01-14 10:25',
          avatar: <SupportIcon />,
          tags: [availableUsers[2]] // Dr. Emily Wilson
        },
        {
          id: 3,
          sender: 'patient',
          message: 'I need a comprehensive checkup including blood tests, ECG, and general health screening.',
          timestamp: '2024-01-14 10:30',
          avatar: <PersonIcon />,
          tags: []
        },
        {
          id: 4,
          sender: 'support',
          name: 'Dr. Emily Wilson',
          message: 'Perfect! We have a comprehensive package that includes all those tests. It takes about 3-4 hours. When would you like to schedule it?',
          timestamp: '2024-01-14 10:35',
          avatar: <SupportIcon />,
          tags: [availableUsers[2]] // Dr. Emily Wilson
        },
        {
          id: 5,
          sender: 'patient',
          message: 'I can come this Saturday morning. What time should I arrive?',
          timestamp: '2024-01-14 10:40',
          avatar: <PersonIcon />,
          tags: []
        },
        {
          id: 6,
          sender: 'support',
          name: 'Dr. Emily Wilson',
          message: 'Great! Please arrive at 8:00 AM on Saturday. You should fast for 12 hours before the test. @SupportTeam will send you a confirmation.',
          timestamp: '2024-01-14 10:45',
          avatar: <SupportIcon />,
          tags: [availableUsers[2], availableUsers[6]] // Dr. Emily Wilson, Support Team
        }
      ]
    }
  ]
};

// Service-specific chat content
const serviceChatContent = {
  'Second Opinion': [
    {
      id: 1,
      sender: 'patient',
      message: 'Hello, I need a second opinion for my cardiology report. Can you help me?',
      timestamp: '2024-01-15 14:30',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 2,
      sender: 'support',
      name: 'Dr. Sarah Johnson',
      message: 'Hello! Of course, I can help you with a second opinion. Could you please share your cardiology report details?',
      timestamp: '2024-01-15 14:35',
      avatar: <SupportIcon />,
      tags: [availableUsers[0]] // Dr. Sarah Johnson
    },
    {
      id: 3,
      sender: 'patient',
      message: 'I have been diagnosed with mild heart valve issues. My doctor suggested medication but I want to explore other options.',
      timestamp: '2024-01-15 14:40',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 4,
      sender: 'support',
      name: 'Dr. Sarah Johnson',
      message: 'I understand your concern. Let me consult with @DrMichael for a cardiology review.',
      timestamp: '2024-01-15 14:45',
      avatar: <SupportIcon />,
      tags: [availableUsers[0], availableUsers[1]] // Dr. Sarah Johnson, Dr. Michael Chen
    }
  ],
  'Appointment Booking': [
    {
      id: 1,
      sender: 'patient',
      message: 'Hi, I need to book an appointment for a general consultation.',
      timestamp: '2024-01-15 16:45',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 2,
      sender: 'support',
      name: 'Dr. Michael Chen',
      message: 'Hello! I can help you book an appointment. What type of consultation do you need?',
      timestamp: '2024-01-15 16:50',
      avatar: <SupportIcon />,
      tags: [availableUsers[1]] // Dr. Michael Chen
    },
    {
      id: 3,
      sender: 'patient',
      message: 'I have been having headaches for the past week and need to see a general physician.',
      timestamp: '2024-01-15 16:55',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 4,
      sender: 'support',
      name: 'Dr. Michael Chen',
      message: 'I understand. Let me check our available slots. @AdminJohn, can you check the schedule for this week?',
      timestamp: '2024-01-15 17:00',
      avatar: <SupportIcon />,
      tags: [availableUsers[1], availableUsers[7]] // Dr. Michael Chen, Admin John
    }
  ],
  'Health Checkup': [
    {
      id: 1,
      sender: 'patient',
      message: 'Hello, I want to schedule a comprehensive health checkup.',
      timestamp: '2024-01-14 10:20',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 2,
      sender: 'support',
      name: 'Dr. Emily Wilson',
      message: 'Hello! I can help you schedule a health checkup. What type of package are you interested in?',
      timestamp: '2024-01-14 10:25',
      avatar: <SupportIcon />,
      tags: [availableUsers[2]] // Dr. Emily Wilson
    },
    {
      id: 3,
      sender: 'patient',
      message: 'I need a comprehensive checkup including blood tests, ECG, and general health screening.',
      timestamp: '2024-01-14 10:30',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 4,
      sender: 'support',
      name: 'Dr. Emily Wilson',
      message: 'Perfect! We have a comprehensive package that includes all those tests. It takes about 3-4 hours. When would you like to schedule it?',
      timestamp: '2024-01-14 10:35',
      avatar: <SupportIcon />,
      tags: [availableUsers[2]] // Dr. Emily Wilson
    },
    {
      id: 5,
      sender: 'patient',
      message: 'I can come this Saturday morning. What time should I arrive?',
      timestamp: '2024-01-14 10:40',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 6,
      sender: 'support',
      name: 'Dr. Emily Wilson',
      message: 'Great! Please arrive at 8:00 AM on Saturday. You should fast for 12 hours before the test. @SupportTeam will send you a confirmation.',
      timestamp: '2024-01-14 10:45',
      avatar: <SupportIcon />,
      tags: [availableUsers[2], availableUsers[6]] // Dr. Emily Wilson, Support Team
    }
  ],
  'Consultation': [
    {
      id: 1,
      sender: 'patient',
      message: 'Hi, I need to consult about my chronic back pain.',
      timestamp: '2024-01-15 14:30',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 2,
      sender: 'support',
      name: 'Dr. Robert Davis',
      message: 'Hello! I can help you with your back pain consultation. How long have you been experiencing this pain?',
      timestamp: '2024-01-15 14:35',
      avatar: <SupportIcon />,
      tags: [availableUsers[3]] // Dr. Robert Davis
    },
    {
      id: 3,
      sender: 'patient',
      message: 'It has been about 6 months now. The pain is mostly in the lower back and gets worse when I sit for long hours.',
      timestamp: '2024-01-15 14:40',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 4,
      sender: 'support',
      name: 'Dr. Robert Davis',
      message: 'I understand. This sounds like it could be related to posture or muscle strain. Let me schedule you with our orthopedic specialist. @DrLisa, can you review this case?',
      timestamp: '2024-01-15 14:45',
      avatar: <SupportIcon />,
      tags: [availableUsers[3], availableUsers[4]] // Dr. Robert Davis, Dr. Lisa Thompson
    }
  ],
  'Follow-up': [
    {
      id: 1,
      sender: 'patient',
      message: 'Hello, I need a follow-up consultation for my previous treatment.',
      timestamp: '2024-01-15 14:30',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 2,
      sender: 'support',
      name: 'Dr. Lisa Thompson',
      message: 'Hello! I can help you schedule a follow-up. How has your recovery been since your last visit?',
      timestamp: '2024-01-15 14:35',
      avatar: <SupportIcon />,
      tags: [availableUsers[4]] // Dr. Lisa Thompson
    },
    {
      id: 3,
      sender: 'patient',
      message: 'I have been feeling much better, but I still have some lingering symptoms.',
      timestamp: '2024-01-15 14:40',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 4,
      sender: 'support',
      name: 'Dr. Lisa Thompson',
      message: 'That\'s good to hear about the improvement. Let me check your previous records and schedule a follow-up. @DrJames, can you review the previous test results?',
      timestamp: '2024-01-15 14:45',
      avatar: <SupportIcon />,
      tags: [availableUsers[4], availableUsers[5]] // Dr. Lisa Thompson, Dr. James Wilson
    }
  ],
  'Test Results': [
    {
      id: 1,
      sender: 'patient',
      message: 'Hi, I\'m calling about my recent blood test results.',
      timestamp: '2024-01-15 14:30',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 2,
      sender: 'support',
      name: 'Dr. James Wilson',
      message: 'Hello! I can help you with your test results. Which tests did you have done?',
      timestamp: '2024-01-15 14:35',
      avatar: <SupportIcon />,
      tags: [availableUsers[5]] // Dr. James Wilson
    },
    {
      id: 3,
      sender: 'patient',
      message: 'I had a complete blood count and lipid profile done last week.',
      timestamp: '2024-01-15 14:40',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 4,
      sender: 'support',
      name: 'Dr. James Wilson',
      message: 'I can see your results. Let me consult with @DrEmily for a detailed analysis of your lipid profile.',
      timestamp: '2024-01-15 14:45',
      avatar: <SupportIcon />,
      tags: [availableUsers[5], availableUsers[2]] // Dr. James Wilson, Dr. Emily Wilson
    }
  ],
  'General Inquiry': [
    {
      id: 1,
      sender: 'patient',
      message: 'Hello, I have a general question about your services.',
      timestamp: '2024-01-15 14:30',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 2,
      sender: 'support',
      name: 'Support Team',
      message: 'Hello! I\'m here to help. What would you like to know about our services?',
      timestamp: '2024-01-15 14:35',
      avatar: <SupportIcon />,
      tags: [availableUsers[6]] // Support Team
    },
    {
      id: 3,
      sender: 'patient',
      message: 'I want to know about the different types of consultations you offer.',
      timestamp: '2024-01-15 14:40',
      avatar: <PersonIcon />,
      tags: []
    },
    {
      id: 4,
      sender: 'support',
      name: 'Support Team',
      message: 'We offer various consultations. Let me connect you with @DrMichael who can provide detailed information about our services.',
      timestamp: '2024-01-15 14:45',
      avatar: <SupportIcon />,
      tags: [availableUsers[6], availableUsers[1]] // Support Team, Dr. Michael Chen
    }
  ]
};

// Available services for dropdown
const availableServices = [
  'Second Opinion',
  'Appointment Booking',
  'Health Checkup',
  'Consultation',
  'Follow-up',
  'Test Results',
  'General Inquiry'
];

function TicketPage() {
  const navigate = useNavigate();
  const { phone } = useParams();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketSelection, setShowTicketSelection] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showTagDialog, setShowTagDialog] = useState(false);

  const tickets = ticketsData[phone] || [];

  const handleTicketSelect = (ticket) => {
    setSelectedTicket(ticket);
    setSelectedService(ticket.service);
    setMessages(ticket.messages);
    setShowTicketSelection(false);
  };

  const handleCreateNewTicket = () => {
    const newTicket = {
      id: `TKT-${Date.now()}`,
      service: 'General Inquiry',
      status: 'Open',
      priority: 'Medium',
      createdAt: new Date().toLocaleString(),
      messages: serviceChatContent['General Inquiry']
    };
    
    setSelectedTicket(newTicket);
    setSelectedService(newTicket.service);
    setMessages(newTicket.messages);
    setShowTicketSelection(false);
  };

  const handleServiceChange = (event) => {
    const newService = event.target.value;
    setSelectedService(newService);
    // Update the ticket's service and change chat content
    setSelectedTicket(prev => ({
      ...prev,
      service: newService
    }));
    // Change chat content based on selected service
    setMessages(serviceChatContent[newService] || serviceChatContent['General Inquiry']);
    setNewMessage('');
  };

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      const message = {
        id: messages.length + 1,
        sender: 'support',
        name: 'Support Team',
        message: newMessage,
        timestamp: new Date().toLocaleString(),
        avatar: <SupportIcon />,
        tags: selectedTags
      };
      setMessages([...messages, message]);
      setNewMessage('');
      setSelectedTags([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleBackToTickets = () => {
    setSelectedTicket(null);
    setShowTicketSelection(true);
    setMessages([]);
    setSelectedService('');
  };

  const handleAddTags = () => {
    setShowTagDialog(true);
  };

  const handleTagConfirm = () => {
    setShowTagDialog(false);
  };

  const handleRemoveTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag.id !== tagToRemove.id));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'success';
      case 'Closed': return 'default';
      default: return 'warning';
    }
  };

  const renderMessageWithTags = (message) => {
    const parts = message.message.split(/(@\w+)/g);
    return parts.map((part, index) => {
      if (part.startsWith('@')) {
        const tag = message.tags.find(t => `@${t.name.split(' ')[0]}` === part);
        if (tag) {
          return (
            <Chip
              key={index}
              label={tag.name}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mx: 0.5, fontSize: '0.75rem' }}
            />
          );
        }
      }
      return part;
    });
  };

  if (showTicketSelection) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Tickets for {phone}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} found
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              size="small"
              onClick={handleCreateNewTicket}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              New Ticket
            </Button>
          </Toolbar>
        </AppBar>

        {/* Tickets List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
          {tickets.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <TicketIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No tickets found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Create a new ticket to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNewTicket}
              >
                Create New Ticket
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {tickets.map((ticket) => (
                <Grid item xs={12} md={6} lg={4} key={ticket.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => handleTicketSelect(ticket)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight={600} color="primary">
                          {ticket.id}
                        </Typography>
                        <Chip
                          label={ticket.status}
                          color={getStatusColor(ticket.status)}
                          size="small"
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Created: {ticket.createdAt}
                        </Typography>
                        <IconButton size="small" color="primary">
                          <ChatIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackToTickets}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedService}
                onChange={handleServiceChange}
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                  '& .MuiSelect-select': {
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1.1rem'
                  }
                }}
              >
                {availableServices.map((service) => (
                  <MenuItem key={service} value={service}>
                    {service}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              {phone} • {selectedTicket.id}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={selectedTicket.priority}
              color={getPriorityColor(selectedTicket.priority)}
              size="small"
              sx={{ color: 'white' }}
            />
            <Chip
              label={selectedTicket.status}
              color={getStatusColor(selectedTicket.status)}
              size="small"
              sx={{ color: 'white' }}
            />
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Messages Area */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Messages List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          <List sx={{ p: 0 }}>
            {messages.map((message, index) => (
              <Box key={message.id}>
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: message.sender === 'patient' ? 'flex-start' : 'flex-end',
                    p: 1
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: message.sender === 'patient' ? 'row' : 'row-reverse',
                      alignItems: 'flex-start',
                      maxWidth: '70%',
                      gap: 1
                    }}
                  >
                    {message.sender === 'patient' && (
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {message.avatar}
                      </Avatar>
                    )}
                    <Box
                      sx={{
                        bgcolor: message.sender === 'patient' ? 'background.paper' : 'primary.main',
                        color: message.sender === 'patient' ? 'text.primary' : 'white',
                        borderRadius: 2,
                        p: 2,
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        border: message.sender === 'patient' ? 1 : 0,
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="body1">
                        {renderMessageWithTags(message)}
                      </Typography>
                      {message.tags && message.tags.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {message.tags.map((tag) => (
                            <Chip
                              key={tag.id}
                              label={tag.name}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.7,
                          display: 'block',
                          mt: 1
                        }}
                      >
                        {message.timestamp}
                      </Typography>
                    </Box>
                    {message.sender === 'support' && (
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                        {message.avatar}
                      </Avatar>
                    )}
                  </Box>
                </ListItem>
                {index < messages.length - 1 && <Divider sx={{ mx: 2 }} />}
              </Box>
            ))}
          </List>
        </Box>

        {/* Message Input */}
        <Paper elevation={3} sx={{ p: 2, m: 2 }}>
          {/* Tags Display */}
          {selectedTags.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedTags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  color="primary"
                  onDelete={() => handleRemoveTag(tag)}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your message... Use @ to tag users"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              variant="outlined"
              onClick={handleAddTags}
              sx={{
                borderRadius: 2,
                minWidth: 48,
                height: 48,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.light' },
              }}
            >
              <TagIcon />
            </Button>
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              sx={{
                borderRadius: 2,
                minWidth: 48,
                height: 48,
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <SendIcon />
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Tag Selection Dialog */}
      <Dialog open={showTagDialog} onClose={() => setShowTagDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Tag Users
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select users to tag in your message
          </Typography>
          <Autocomplete
            multiple
            options={availableUsers}
            getOptionLabel={(option) => `${option.name} (${option.role})`}
            value={selectedTags}
            onChange={(event, newValue) => setSelectedTags(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search users..."
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                  {option.avatar}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.role}
                  </Typography>
                </Box>
              </Box>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  size="small"
                  color="primary"
                />
              ))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTagDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleTagConfirm} variant="contained">
            Confirm Tags
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TicketPage; 